<template>
  <MultiSelectFilter
    :show-menu="showMenu"
    :title="title"
    :subtitle="subtitle"
    :numberOfSelectedValues="numberOfSelectedValues"
    @handleMenu="handleOpenMenu"
    @clearFilters="onClearFilters"
  >
    <BbPopover
      :model-value="showMenu"
      :hideCloseButton="true"
      location="bottom"
      contentClass="authored-filters-popup"
      width="57%"
      @update:modelValue="handleOpenMenu(false)"
    >
      <template #popover>
        <div class="popover-container">
          <FilterFormMultiselect :filtersConfig="filtersConfig" :withSearch="true" :type="filterType" />
        </div>
      </template>
    </BbPopover>
  </MultiSelectFilter>
</template>

<script>
import { computed, inject, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { FILTER_TYPE } from '~/constants/documents';

import useAuthoring from '~/composables/useAuthoring';
import useFormFilters from '~/composables/useFormFilters';
import MultiSelectFilter from '~/components/conversation/authoring/common/MultiSelectFilter.vue';
import FilterFormMultiselect from '~/services/filter-form-service/components/FilterFormMultiselect.vue';

export default {
  components: {
    FilterFormMultiselect,
    MultiSelectFilter,
  },
  setup() {
    const filterService = inject('filterFormService');

    const { t } = useI18n();
    const { expandedFilters, setExpandedFilters } = useAuthoring();
    const { setFiltersObject, formFilters } = useFormFilters();

    const showMenu = ref(false);
    const filtersConfig = ref([]);

    const filterType = FILTER_TYPE.document;

    const title = t('common.mediaFilters');

    const formFiltersForType = computed(() => formFilters.value[filterType] || {});

    const numberOfSelectedValues = computed(() => Object.values(formFiltersForType.value).flat().length);

    const subtitle = computed(() =>
      numberOfSelectedValues.value ? `+${numberOfSelectedValues.value}` : t('author.filters.pickOneOrMore'),
    );

    const handleOpenMenu = async (value) => {
      showMenu.value = value;
      if (value) {
        setExpandedFilters(value);
      }
    };

    const onClearFilters = () => {
      // Clear only 'document' type filters
      setFiltersObject({ [filterType]: {} });
    };

    const filterInit = async () => {
      const filterForm = await filterService.getProductAndCountryFormFilters();

      filtersConfig.value = filterForm;

      if (formFiltersForType.value) {
        filtersConfig.value = await filterService.getUpdatedProductAndCountryFilters(
          filterType,
          formFiltersForType.value,
        );
      }
    };

    onMounted(async () => {
      await filterInit();
    });

    watch(
      () => formFiltersForType.value,
      async (newVals, oldVals) => {
        if (JSON.stringify(newVals) !== JSON.stringify(oldVals)) {
          filtersConfig.value = await filterService.getUpdatedProductAndCountryFilters(
            filterType,
            formFiltersForType.value,
          );
        }
      },
      { deep: true },
    );

    return {
      t,
      expandedFilters,
      title,
      subtitle,
      showMenu,
      handleOpenMenu,
      onClearFilters,
      numberOfSelectedValues,
      filtersConfig,
      filterType,
    };
  },
};
</script>

<style scoped lang="scss">
.popover-container {
  display: flex;
  flex-direction: row;
  gap: $bb-spacing-6;
}
</style>
